/* eslint-disable eqeqeq */
/* eslint-disable no-unused-vars */
/* TreeExpressionField.jsx */
import {
  AddIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DeleteIcon,
} from "@chakra-ui/icons";
import {
  Box,
  Collapse,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Text,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import ReactSelect from "react-select";
import { selectStyles } from "../../../utils/selectStyel";

export default function ExpressionField({ path, level = 0, selectedNode }) {
  const { control } = useFormContext();

  return (
    <Box
      pl={level * 2}
      borderLeft={level > 0 ? "1px solid gray" : "none"}
      ml={level > 0 ? 1 : 0}
    >
      <ExpressionTypeSelect path={path} />
      <ExpressionDetails
        path={path}
        level={level}
        selectedNode={selectedNode}
      />
    </Box>
  );
}

/* -------------------- TYPE SELECT -------------------- */

function ExpressionTypeSelect({ path }) {
  const { control } = useFormContext();
  return (
    <FormControl mb={2}>
      <FormLabel fontSize="sm">Expression Type</FormLabel>
      <Controller
        control={control}
        name={`${path}.TYPE`}
        render={({ field }) => (
          <ReactSelect
            {...field}
            options={[
              { value: "REFERENCE", label: "Reference" },
              { value: "LITERAL", label: "Literal" },
              { value: "OPERATION", label: "Operation" },
            ]}
            placeholder="Select Type"
            value={
              field.value
                ? {
                    value: field.value,
                    label:
                      field.value.charAt(0).toUpperCase() +
                      field.value.slice(1).toLowerCase(),
                  }
                : null
            }
            onChange={(selected) => field.onChange(selected?.value || "")}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={selectStyles}
          />
        )}
      />
    </FormControl>
  );
}

/* -------------------- DETAILS -------------------- */

function ExpressionDetails({ path, level, selectedNode }) {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={`${path}.TYPE`}
      render={({ field }) => {
        const type = field.value;

        if (type === "REFERENCE" || type === "LITERAL") {
          return (
            <FormControl mt={1}>
              <FormLabel m={0} fontSize="sm" my={1}>
                Value:
              </FormLabel>
              <Controller
                control={control}
                name={`${path}.VALUE`}
                render={({ field }) => (
                  <Input
                    {...field}
                    w="100%"
                    bg="white"
                    borderColor="gray.300"
                  />
                )}
              />
            </FormControl>
          );
        }

        if (type === "OPERATION") {
          return (
            <OperationTree
              path={path}
              level={level}
              selectedNode={selectedNode}
            />
          );
        }

        return null;
      }}
    />
  );
}

/* -------------------- OPERATION TREE -------------------- */

function OperationTree({ path, level, selectedNode }) {
  const { control } = useFormContext();
  const argsArray = useFieldArray({
    control,
    name: `${path}.ARGS`,
  });

  /* Dropdown options for operators */
  const operatorOptions = [
    { value: "Sum", label: "Sum (sum of all args)" },
    { value: "+", label: "+" },
    { value: "-", label: "-" },
    { value: "*", label: "*" },
    { value: "/", label: "/" },
    { value: "DateDiff", label: "Date Difference" },
    { value: "Concat", label: "Concatenate"}
  ];

  return (
    <Box mt={2}>
      {/* Operator Select */}
      <FormControl mb={2}>
        <FormLabel m={0} fontSize="sm" my={1}>
          Operator:
        </FormLabel>

        <Controller
          control={control}
          name={`${path}.OP`}
          render={({ field }) => (
            <ReactSelect
              {...field}
              options={operatorOptions}
              placeholder="Select operator"
              value={
                operatorOptions.find((op) => op.value === field.value) || null
              }
              onChange={(selected) => field.onChange(selected?.value || "")}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectStyles}
            />
          )}
        />
      </FormControl>

      {/* Arguments */}
      <Box>
        <Flex align="center" gap={2} mb={1}>
          <Text fontSize="sm" fontWeight="medium">
            Arguments
          </Text>
          <IconButton
            size="xs"
            icon={<AddIcon />}
            aria-label="Add argument"
            colorScheme="purple"
            borderRadius="2xl"
            onClick={() => argsArray.prepend({ TYPE: "", VALUE: "" })}
          />
        </Flex>

        <Wrap spacing={3}>
          {argsArray.fields.map((item, idx) => (
            <WrapItem key={item.id}>
              <TreeArgNode
                path={`${path}.ARGS.${idx}`}
                level={level + 1}
                idx={idx}
                remove={() => argsArray.remove(idx)}
                selectedNode={selectedNode}
                nodeData={item}
              />
            </WrapItem>
          ))}
        </Wrap>
      </Box>
    </Box>
  );
}

/* -------------------- ARG NODE -------------------- */

function TreeArgNode({ path, level, idx, remove, selectedNode, nodeData }) {
  const [isOpen, setIsOpen] = useState(level < 2);
  const bgColors = ["gray.50", "gray.100", "gray.200", "gray.300"];
  const defaultBg = bgColors[level] || "gray.300";
  const isSelected =
    selectedNode && nodeData && nodeData.VALUE == selectedNode.value;
  const bgColor = isSelected ? "yellow.100" : defaultBg;
  return (
    <Box
      pl={2}
      borderLeft="1px solid gray"
      ml={1}
      mb={2}
      bg={bgColor}
      borderRadius="md"
      p={3}
      boxShadow="md"
      transition="all 0.2s ease"
      _hover={{
        boxShadow: "lg",
        transform: "scale(1.02)",
      }}
      border={isSelected ? "2px solid yellow" : "1px solid gray"}
    >
      <Flex align="center" gap={2} justifyContent="space-between">
        <Flex align="center" gap={2}>
          <IconButton
            size="sm"
            border="none"
            icon={isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
            onClick={() => setIsOpen(!isOpen)}
          />
          <Text fontSize="sm" fontWeight="medium">
            Arg #{idx + 1}
          </Text>
        </Flex>

        <IconButton
          size="xs"
          colorScheme="red"
          border="none"
          icon={<DeleteIcon />}
          onClick={remove}
          borderRadius="2xl"
        />
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <Box mt={1}>
          <ExpressionField path={path} level={level} />
        </Box>
      </Collapse>
    </Box>
  );
}
